/**
 * Le style de tableau du lecteur de forme (#1862) : un paragraphe de cellule
 * tient une part de sa forme du style de son tableau (`w:tblStyle`) — le style
 * entier, et ses formats conditionnels (`w:tblStylePr` : première ligne,
 * première colonne…), qui portent le plus souvent le gras des en-têtes.
 *
 * Mesuré sur SuperDoc 2.15.1 (2026-10-02), et rejoué à chaque montée de
 * version par `tests/ws-mocked/canvas-lecture-de-forme-cas-difficiles.spec.ts` :
 * - un format conditionnel s'applique selon la POSITION de la cellule et le
 *   `w:tblLook` du tableau ; `w:cnfStyle` n'est pas lu ;
 * - ⚠️ sans `w:tblLook`, l'éditeur applique première ligne, première colonne et
 *   bandes horizontales (le masque `04A0`), là où la norme dit « bandes de
 *   lignes et de colonnes, rien d'autre ». Le juge est l'écran ;
 * - les formats conditionnels passent devant le style entier, y compris ceux
 *   d'un style parent devant le style entier de l'enfant ;
 * - un tableau dans une cellule ne suit que son propre style.
 *
 * ⚠️ Ce que ce module ne tranche PAS, et rend non lisible :
 * - deux formats conditionnels qui s'appliquent à la même cellule et se
 *   contredisent : l'éditeur fait gagner la première ligne sur la première
 *   colonne, la norme applique la première colonne APRÈS la première ligne ;
 * - tout réglage porté par des bandes quand le tableau les active : l'éditeur
 *   compte la ligne d'en-tête dans la première bande (mesuré, non sourcé côté
 *   norme) ;
 * - tout réglage porté par un format conditionnel dans un tableau à cellules
 *   fusionnées verticalement (`w:vMerge`), à lignes décalées (`w:gridBefore`,
 *   `w:gridAfter`), ou dont une ligne ou une cellule vit sous une enveloppe
 *   (contrôle de contenu) : la position d'une cellule n'y est pas mesurée.
 *
 * Sources de la norme (ISO/IEC 29500-1, reprise par Microsoft Learn, vérifiées
 * le 2026-10-02) :
 * - ordre d'application des formats conditionnels, chacun écrasant les
 *   précédents — https://learn.microsoft.com/en-us/dotnet/api/documentformat.openxml.wordprocessing.tablestyleproperties
 * - `w:tblLook`, ses six attributs et son défaut —
 *   https://learn.microsoft.com/en-us/dotnet/api/documentformat.openxml.wordprocessing.tablelook
 */

import { isWord, type XmlNode } from '@hector/docx-integrity';
import { allume, attribut, enfant, porteur, type Lecture, type Lu, type Niveau } from './cascadeDeForme';

/** Une cellule (`w:tc`) et sa situation dans son tableau. */
export interface CelluleDeTableau {
  noeud: XmlNode;
  tableau: XmlNode;
  premiereLigne: boolean;
  derniereLigne: boolean;
  premiereColonne: boolean;
  derniereColonne: boolean;
  /**
   * Le tableau fusionne des cellules verticalement ou décale des lignes : les
   * quatre positions ci-dessus ne sont alors que le rang de la cellule dans le
   * fichier, pas sa place à l'écran.
   */
  positionIncertaine: boolean;
}

/** Ce qu'il faut d'un style nommé pour en lire la part « tableau ». */
interface StyleDeTableau {
  /** L'élément `w:style` : ses `w:tblStylePr` portent les formats conditionnels. */
  noeud: XmlNode;
  pPr: XmlNode | undefined;
  rPr: XmlNode | undefined;
}

/** Les éléments `nom` d'un conteneur, au travers des enveloppes (contrôle de contenu, révision), sans entrer dans un tableau imbriqué. */
function* elementsDe(conteneur: XmlNode, nom: string): Generator<XmlNode> {
  for (const candidat of conteneur.children) {
    if (isWord(candidat, nom)) yield candidat;
    else if (!isWord(candidat, 'tbl') && !isWord(candidat, 'tc') && !isWord(candidat, 'p')) yield* elementsDe(candidat, nom);
  }
}

/** Les lignes d'un tableau et leurs cellules, dans l'ordre du document, au travers des enveloppes. */
export function lignesDuTableau(tableau: XmlNode): Array<{ ligne: XmlNode; cellules: XmlNode[] }> {
  return [...elementsDe(tableau, 'tr')].map((ligne) => ({ ligne, cellules: [...elementsDe(ligne, 'tc')] }));
}

/** Les cellules d'un tableau, dans l'ordre du document, chacune avec sa situation. */
export function cellulesDuTableau(tableau: XmlNode): CelluleDeTableau[] {
  const lignes = lignesDuTableau(tableau);
  const directs = (conteneur: XmlNode, nom: string): number => conteneur.children.filter((candidat) => isWord(candidat, nom)).length;
  // Mesuré : lignes et cellules en enfants directs, cellules fusionnées en largeur (`w:gridSpan`) comprises.
  // Non mesuré, donc incertain : une ligne ou une cellule sous une enveloppe, une ligne décalée, une fusion verticale.
  const positionIncertaine = directs(tableau, 'tr') !== lignes.length || lignes.some(({ ligne, cellules }) =>
    directs(ligne, 'tc') !== cellules.length
    || enfant(enfant(ligne, 'trPr'), 'gridBefore') !== undefined
    || enfant(enfant(ligne, 'trPr'), 'gridAfter') !== undefined
    || cellules.some((cellule) => enfant(enfant(cellule, 'tcPr'), 'vMerge') !== undefined));
  return lignes.flatMap(({ cellules }, rangDeLigne) => {
    return cellules.map((noeud, rangDeCellule): CelluleDeTableau => ({
      noeud,
      tableau,
      premiereLigne: rangDeLigne === 0,
      derniereLigne: rangDeLigne === lignes.length - 1,
      premiereColonne: rangDeCellule === 0,
      derniereColonne: rangDeCellule === cellules.length - 1,
      positionIncertaine,
    }));
  });
}

/** L'identifiant du style posé sur un tableau, ou `undefined` s'il n'en nomme pas. */
export function styleDuTableau(tableau: XmlNode): string | undefined {
  return attribut(enfant(enfant(tableau, 'tblPr'), 'tblStyle'), 'val');
}

/**
 * Les bits du masque `w:tblLook@w:val`, repli des attributs nommés. Valeurs non
 * sourcées par la norme en ligne : chacune est MESURÉE à l'écran par la garde
 * « fichier contre écran » (tableaux « masque seul » du Word importé de référence).
 */
const BIT_DU_REGARD = { firstRow: 0x0020, lastRow: 0x0040, firstColumn: 0x0080, lastColumn: 0x0100, noHBand: 0x0200, noVBand: 0x0400 } as const;
/** Le masque que prend l'éditeur quand le tableau n'a pas de `w:tblLook` (mesuré par la même garde : tableaux « sans regard »). */
const REGARD_PAR_DEFAUT = 0x04a0;

/**
 * Les formats conditionnels qui visent une cellule : `certains` s'y appliquent,
 * `incertains` sont ceux dont l'application n'est pas établie — les bandes
 * actives, et tous les formats actifs d'un tableau à position incertaine.
 */
function conditionnelsDeLaCellule(cellule: CelluleDeTableau): { certains: string[]; incertains: string[] } {
  const regard = enfant(enfant(cellule.tableau, 'tblPr'), 'tblLook');
  const masque = Number.parseInt(attribut(regard, 'val') ?? '', 16);
  const actif = (nom: keyof typeof BIT_DU_REGARD): boolean =>
    allume(attribut(regard, nom), ((Number.isNaN(masque) ? REGARD_PAR_DEFAUT : masque) & BIT_DU_REGARD[nom]) !== 0);
  const premiereLigne = actif('firstRow') && cellule.premiereLigne;
  const derniereLigne = actif('lastRow') && cellule.derniereLigne;
  const premiereColonne = actif('firstColumn') && cellule.premiereColonne;
  const derniereColonne = actif('lastColumn') && cellule.derniereColonne;
  const retenus = (types: ReadonlyArray<readonly [string, boolean]>): string[] => types.flatMap(([type, retenu]) => (retenu ? [type] : []));
  const bandes = retenus([
    ['band1Horz', !actif('noHBand')],
    ['band2Horz', !actif('noHBand')],
    ['band1Vert', !actif('noVBand')],
    ['band2Vert', !actif('noVBand')],
  ]);
  if (cellule.positionIncertaine) {
    const bords = retenus([['firstRow', actif('firstRow')], ['lastRow', actif('lastRow')], ['firstCol', actif('firstColumn')], ['lastCol', actif('lastColumn')]]);
    return { certains: [], incertains: [...bords, 'nwCell', 'neCell', 'swCell', 'seCell', ...bandes] };
  }
  return {
    certains: retenus([
      ['firstRow', premiereLigne],
      ['lastRow', derniereLigne],
      ['firstCol', premiereColonne],
      ['lastCol', derniereColonne],
      ['nwCell', premiereLigne && premiereColonne],
      ['neCell', premiereLigne && derniereColonne],
      ['swCell', derniereLigne && premiereColonne],
      ['seCell', derniereLigne && derniereColonne],
    ]),
    incertains: bandes,
  };
}

/**
 * Les niveaux que le style de tableau ajoute à la cascade d'un paragraphe de
 * cellule, du plus fort au plus faible : ses formats conditionnels, puis le
 * style entier et ceux dont il hérite.
 *
 * @param chaine Le style du tableau et ceux dont il hérite, du plus proche au plus lointain ; vide : aucun style.
 * @param part `pPr` pour la forme de paragraphe, `rPr` pour la forme de texte.
 */
export function niveauxDuStyleDeTableau(chaine: readonly StyleDeTableau[], cellule: CelluleDeTableau, part: 'pPr' | 'rPr'): Niveau[] {
  if (chaine.length === 0) return [];
  const { certains, incertains } = conditionnelsDeLaCellule(cellule);
  // Ce qu'un format conditionnel dit d'un réglage : le style le plus proche qui le définit, comme pour le style entier.
  const lireLeConditionnel = <Valeur>(type: string, lire: Lecture<Valeur>): Lu<Valeur> => {
    for (const style of chaine) {
      const conditionnel = style.noeud.children.find((candidat) => isWord(candidat, 'tblStylePr') && attribut(candidat, 'type') === type);
      const lu = lire(enfant(conditionnel, part));
      if (lu !== undefined) return lu;
    }
    return undefined;
  };
  const arbitre = <Valeur>(lire: Lecture<Valeur>): Lu<Valeur> => {
    if (incertains.some((type) => lireLeConditionnel(type, lire) !== undefined)) return { nonLisible: 'style_de_tableau' };
    const [premier, ...autres] = certains.flatMap((type) => lireLeConditionnel(type, lire) ?? []);
    if (premier === undefined || 'nonLisible' in premier) return premier;
    return autres.every((autre) => 'valeur' in autre && autre.valeur === premier.valeur) ? premier : { nonLisible: 'style_de_tableau' };
  };
  return [{ sorte: 'arbitre', origine: 'style', arbitre }, ...chaine.map((style) => porteur('style', style[part]))];
}
